import React, { useState, useEffect, useRef } from "react";
import "./PhotoCounts.css";
import { pruneBumps, addBump, confirmBump, removeBump, mergeCounts } from "./logic";
import { computePace } from "../../../../../shared/minimePace";

/**
 * PhotoCounts — a small glass pill showing the event's photos + copies counts,
 * IDENTICAL to the admin panel's guests-tab summary ("N תמונות · M פסלונים"):
 * the backend endpoint counts the same folders with the same copies semantics
 * (client request, 2026-08-01). Rendered beside EventCountdown inside the
 * TopStatusPills row.
 *
 * Freshness: polls every 60s (server side is Redis-cached ~20s and shared
 * across tablets) + optimistic bumps the moment THIS device's send is
 * acknowledged — bumpPhotoCounts()/unbumpPhotoCounts() are called from
 * CameraCapture's send pipeline; the next poll reconciles. Old server without
 * the endpoint (404) → the pill simply doesn't render.
 */
const REFRESH_MS = 60000;

// Module-scope per-event store: survives the component's unmounts on every
// camera/edit transition (same pattern as EventCountdown.lastKnown), and lets
// CameraCapture bump counts without prop-drilling into the pill.
const store = {}; // eventName -> { server: {photos, copies}|null, bumps: [], supported: bool|null }
const listeners = new Set();

const getState = (eventName) => {
  if (!store[eventName]) {
    store[eventName] = { server: null, bumps: [], supported: null };
  }
  return store[eventName];
};

const notify = () => listeners.forEach((fn) => fn((n) => n + 1));

/** Optimistic +1 photo (+copies) for a send this device just fired — call on
 * server ack; id = the send's capture id (idempotent). */
export const bumpPhotoCounts = (eventName, id, copies) => {
  const st = getState(eventName);
  st.bumps = addBump(st.bumps, id, copies, Date.now());
  notify();
};

/** Mark a send's bump as confirmed-saved — starts its absorb clock (the next
 * fresh-enough server count provably includes it). */
export const confirmPhotoCounts = (eventName, id) => {
  const st = getState(eventName);
  st.bumps = confirmBump(st.bumps, id, Date.now());
  notify();
};

/** Cancel a send's optimistic bump (its upload ultimately failed). */
export const unbumpPhotoCounts = (eventName, id) => {
  const st = getState(eventName);
  st.bumps = removeBump(st.bumps, id);
  notify();
};

/** Subscribe to the module store — shared by the pill, the pace alert banner
 * and the edit-page pace dot, so they all re-render on the same updates. */
export const usePhotoCountsSnapshot = (eventName) => {
  const [, forceRender] = useState(0);
  useEffect(() => {
    const listener = (updater) => forceRender(updater);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);
  return getState(eventName);
};

/** Current pace verdict for the event (silent when the server predates the
 * pace fields or the schedule is unknown). */
export const getPace = (eventName) => computePace(getState(eventName).server || {});

const fetchCountsFor = async (serverUrl, eventName) => {
  if (!serverUrl || !eventName) return;
  // Ordering guard: a poll stalled on venue Wi-Fi must never resolve late
  // and overwrite a NEWER count with an older one (the counter only grows —
  // a stale overwrite is user-visible). Each fetch takes a sequence number;
  // only the latest may apply. The abort timeout keeps a stuck request from
  // outliving its successor.
  const st0 = getState(eventName);
  const seq = (st0.fetchSeq = (st0.fetchSeq || 0) + 1);
  const controller = new AbortController();
  const abortTimer = setTimeout(() => controller.abort(), 20000);
  try {
    const resp = await fetch(
      `${serverUrl}/api/minime/photo-counts?event_name=${encodeURIComponent(eventName)}`,
      { signal: controller.signal }
    );
    const st = getState(eventName);
    if (st.fetchSeq !== seq) return; // a newer poll superseded this one
    if (resp.status === 404) {
      st.supported = false; // old server / unknown event — hide quietly
      notify();
      return;
    }
    if (!resp.ok) return;
    const data = await resp.json();
    if (st.fetchSeq !== seq) return;
    if (typeof data.photos === "number" && typeof data.copies === "number") {
      st.server = {
        photos: data.photos,
        copies: data.copies,
        // pace input (tier 2) — undefined on an older server → pace "silent"
        elapsedMinutes: data.elapsedMinutes,
      };
      st.bumps = pruneBumps(st.bumps, Date.now());
      st.supported = true;
      notify();
    }
  } catch {
    /* network blip / abort — keep last known */
  } finally {
    clearTimeout(abortTimer);
  }
};

/** Poll the counts endpoint while the calling component is mounted. Shared by
 * the pill AND the edit-stage PaceDot — without this the dot showed a verdict
 * frozen at edit-entry for the whole editing session (review 2026-08-01).
 * The store's fetchSeq guard makes overlapping pollers harmless. */
export const usePhotoCountsPolling = (serverUrl, eventName) => {
  useEffect(() => {
    fetchCountsFor(serverUrl, eventName);
    const poll = setInterval(() => fetchCountsFor(serverUrl, eventName), REFRESH_MS);
    return () => clearInterval(poll);
  }, [serverUrl, eventName]);
};

const PhotoCounts = ({ serverUrl, eventName }) => {
  const [, forceRender] = useState(0);
  const stateRef = useRef(getState(eventName));
  stateRef.current = getState(eventName);

  useEffect(() => {
    const listener = (updater) => forceRender(updater);
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  usePhotoCountsPolling(serverUrl, eventName);

  const st = stateRef.current;
  if (st.supported === false || !st.server) return null;

  const { photos, copies } = mergeCounts(st.server, st.bumps);
  // Pace tint on the pill itself (client spec: green stays green, orange =
  // attention, red = take initiative; fast gets its own accent). silent/warmup
  // render the neutral pill.
  const pace = computePace(st.server);
  const paceClass =
    pace.state === "silent" || pace.state === "warmup"
      ? ""
      : ` pace-${pace.state}${pace.fast ? " pace-fast" : ""}`;

  return (
    <div className={`minime-photo-counts rtl${paceClass}`}>
      {photos === 1 ? "תמונה אחת" : `${photos} תמונות`} · {copies === 1 ? "פסלון אחד" : `${copies} פסלונים`}
    </div>
  );
};

export default PhotoCounts;
